import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts } from '@/constants/theme';
import {
  createSubject,
  fetchSubjectsForSchool,
  type SubjectListItem,
  updateSubject,
} from '@/lib/subjects-admin';
import { useAuth } from '@/providers/auth-provider';

type ModalMode = 'create' | 'edit';

export default function AdminSubjectsScreen() {
  const { profile } = useAuth();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;
  const [subjects, setSubjects] = useState<SubjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [selectedSubject, setSelectedSubject] = useState<SubjectListItem | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const modalTitle = useMemo(
    () => (modalMode === 'create' ? 'Add subject' : 'Edit subject'),
    [modalMode]
  );

  const loadSubjects = useCallback(async () => {
    if (!profile?.school_id) {
      setLoading(false);
      return;
    }

    try {
      const rows = await fetchSubjectsForSchool(profile.school_id);
      setSubjects(rows);
    } catch (error) {
      Alert.alert('Unable to load subjects', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [profile?.school_id]);

  useEffect(() => {
    void loadSubjects();
  }, [loadSubjects]);

  const subjectStats = useMemo(() => {
    const total = subjects.length;
    const coded = subjects.filter((item) => item.code?.trim()).length;
    const open = total - coded;

    return [
      { label: 'Total', value: String(total) },
      { label: 'Coded', value: String(coded) },
      { label: 'Open', value: String(open) },
    ];
  }, [subjects]);

  const subjectsMissingCode = useMemo(
    () => subjects.filter((item) => !item.code?.trim()),
    [subjects]
  );

  const resetForm = () => {
    setSelectedSubject(null);
    setName('');
    setCode('');
    setModalMode('create');
  };

  const openCreateModal = () => {
    resetForm();
    setModalVisible(true);
  };

  const openEditModal = (subject: SubjectListItem) => {
    setSelectedSubject(subject);
    setModalMode('edit');
    setName(subject.name);
    setCode(subject.code ?? '');
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    resetForm();
  };

  const handleCreateSubject = async () => {
    if (!profile?.school_id) {
      Alert.alert('School missing', 'Your account is missing school access.');
      return;
    }

    if (!name.trim()) {
      Alert.alert('Missing name', 'Enter a subject name.');
      return;
    }

    setSubmitting(true);

    try {
      await createSubject({
        code,
        name,
        schoolId: profile.school_id,
      });

      closeModal();
      await loadSubjects();
      Alert.alert('Subject added', 'The subject was created successfully.');
    } catch (error) {
      Alert.alert('Create failed', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubject = async () => {
    if (!profile?.school_id || !selectedSubject) {
      Alert.alert('Subject missing', 'Select a subject first.');
      return;
    }

    if (!name.trim()) {
      Alert.alert('Missing name', 'Enter a subject name.');
      return;
    }

    setSubmitting(true);

    try {
      await updateSubject({
        code,
        name,
        schoolId: profile.school_id,
        subjectId: selectedSubject.id,
      });

      closeModal();
      await loadSubjects();
      Alert.alert('Subject updated', 'The subject details were updated.');
    } catch (error) {
      Alert.alert('Update failed', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (modalMode === 'create') {
      void handleCreateSubject();
      return;
    }

    void handleEditSubject();
  };

  const desktopModalSheet = (
    <Modal animationType="fade" transparent visible={modalVisible} onRequestClose={closeModal}>
      <View style={styles.desktopModalBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={closeModal} />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.desktopModalKeyboard}>
          <View style={styles.desktopModalShell}>
            <View style={styles.desktopModalCard}>
              <ScrollView
                contentContainerStyle={styles.desktopModalScroll}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}>
                <View style={styles.desktopModalHeader}>
                  <View>
                    <Text style={styles.desktopModalHeaderLabel}>
                      {modalMode === 'create' ? 'New subject' : 'Edit subject'}
                    </Text>
                    <Text style={styles.desktopModalHeaderValue}>
                      {modalMode === 'create' ? 'Add a subject' : 'Update the subject'}
                    </Text>
                    <Text style={styles.desktopModalHeaderSubtext}>
                      Keep the subject name and code clean for assignment and paper scanning.
                    </Text>
                  </View>
                  <Pressable onPress={closeModal} style={styles.desktopModalClose}>
                    <MaterialIcons name="close" size={18} color="#163828" />
                  </Pressable>
                </View>

                <View style={styles.desktopModalForm}>
                  <InputField
                    autoCapitalize="words"
                    label="SUBJECT NAME"
                    onChangeText={setName}
                    placeholder="e.g. Mathematics"
                    value={name}
                  />
                  <InputField
                    autoCapitalize="characters"
                    label="SUBJECT CODE"
                    onChangeText={setCode}
                    placeholder="e.g. MATH-101"
                    value={code}
                  />
                </View>

                <View style={styles.desktopModalActions}>
                  <Pressable onPress={closeModal} style={styles.desktopModalCancel}>
                    <Text style={styles.desktopModalCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable onPress={handleSubmit} style={styles.desktopModalSave}>
                    {submitting ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.desktopModalSaveText}>
                        {modalMode === 'create' ? 'Save subject' : 'Update subject'}
                      </Text>
                    )}
                  </Pressable>
                </View>
              </ScrollView>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  if (isDesktop) {
    return (
      <>
        <View style={styles.desktopPage}>
          <ScrollView
            contentContainerStyle={styles.desktopScroll}
            showsVerticalScrollIndicator={false}
            style={styles.desktopScrollView}>
            <View style={styles.desktopHero}>
              <View style={styles.desktopHeroTop}>
                <View style={styles.desktopHeroCopy}>
                  <Text style={styles.desktopEyebrow}>Admin / Subjects</Text>
                  <Text style={styles.desktopTitle}>Subject structure</Text>
                  <Text style={styles.desktopSubtitle}>
                    Keep subject names and codes organized from one workspace.
                  </Text>
                </View>

                <Pressable onPress={openCreateModal} style={styles.desktopHeaderBtn}>
                  <MaterialIcons name="add" size={20} color="#F4EFE6" />
                  <Text style={styles.desktopHeaderBtnText}>Add subject</Text>
                </Pressable>
              </View>

              <View style={styles.desktopHeroPanel}>
                <View style={styles.desktopHeroPanelMain}>
                  <Text style={styles.desktopHeroDate}>Workspace overview</Text>
                  <Text style={styles.desktopHeroHeadline}>Subject codes and catalog cleanup.</Text>
                  <Text style={styles.desktopHeroBody}>
                    Use the desktop layout to scan the catalog, find missing codes, and add or edit
                    records faster.
                  </Text>
                </View>

                <View style={styles.desktopStatsGrid}>
                  {subjectStats.map((item, index) => (
                    <View
                      key={item.label}
                      style={[styles.desktopStatCard, index > 0 ? styles.desktopStatDivider : null]}>
                      <Text style={styles.desktopStatValue}>{item.value}</Text>
                      <Text style={styles.desktopStatLabel}>{item.label}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>

            <View style={styles.desktopGrid}>
              <View style={styles.desktopColumnLarge}>
                <View style={styles.desktopSection}>
                  <View style={styles.sectionHeadDesktop}>
                    <View>
                      <Text style={styles.sectionTitleDesktop}>All subjects</Text>
                      <Text style={styles.sectionCaptionDesktop}>
                        Select a row to edit subject details.
                      </Text>
                    </View>
                    <Pressable onPress={openCreateModal}>
                      <Text style={styles.linkTextDesktop}>Add subject</Text>
                    </Pressable>
                  </View>

                  {loading ? (
                    <View style={styles.desktopLoadingWrap}>
                      <ActivityIndicator color="#163828" />
                    </View>
                  ) : subjects.length === 0 ? (
                    <View style={styles.desktopEmptyState}>
                      <View style={styles.desktopEmptyIcon}>
                        <MaterialIcons name="library-books" size={26} color="#163828" />
                      </View>
                      <Text style={styles.desktopEmptyTitle}>No subjects found</Text>
                      <Text style={styles.desktopEmptyText}>
                        Create the first subject to start organizing the catalog.
                      </Text>
                      <Pressable onPress={openCreateModal} style={styles.desktopEmptyButton}>
                        <Text style={styles.desktopEmptyButtonText}>Create subject</Text>
                      </Pressable>
                    </View>
                  ) : (
                    <View style={styles.desktopListBlock}>
                      {subjects.map((subject, index) => {
                        const hasCode = Boolean(subject.code?.trim());

                        return (
                          <Pressable
                            key={subject.id}
                            onPress={() => openEditModal(subject)}
                            style={[
                              styles.desktopListRow,
                              index < subjects.length - 1 ? styles.desktopListDivider : null,
                            ]}>
                            <View style={styles.desktopRowIndex}>
                              <Text style={styles.desktopRowIndexText}>
                                {subject.name.slice(0, 1).toUpperCase()}
                              </Text>
                            </View>

                            <View style={styles.desktopRowCopy}>
                              <Text style={styles.desktopRowTitle} numberOfLines={1}>
                                {subject.name}
                              </Text>
                              <Text style={styles.desktopRowMeta} numberOfLines={1}>
                                {hasCode ? subject.code : 'No code'}
                              </Text>
                            </View>

                            <View style={styles.desktopRowRight}>
                              <View
                                style={[
                                  styles.desktopRowTag,
                                  { backgroundColor: hasCode ? '#EAF3FF' : '#EEF4EF' },
                                ]}>
                                <Text
                                  style={[
                                    styles.desktopRowTagText,
                                    { color: hasCode ? '#0F4C81' : '#5D7261' },
                                  ]}>
                                  {hasCode ? 'Coded' : 'Open'}
                                </Text>
                              </View>
                              <Text style={styles.desktopRowCount}>
                                {hasCode ? 'Code assigned' : 'Tap to add code'}
                              </Text>
                            </View>
                          </Pressable>
                        );
                      })}
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.desktopColumnSide}>
                <View style={styles.desktopSection}>
                  <View style={styles.sectionHeadDesktop}>
                    <View>
                      <Text style={styles.sectionTitleDesktop}>Code coverage</Text>
                      <Text style={styles.sectionCaptionDesktop}>
                        Subjects that still need a code.
                      </Text>
                    </View>
                  </View>

                  {loading ? (
                    <View style={styles.desktopLoadingWrap}>
                      <ActivityIndicator color="#163828" />
                    </View>
                  ) : subjectsMissingCode.length === 0 ? (
                    <View style={styles.desktopEmptyState}>
                      <Text style={styles.desktopEmptyTitle}>All subjects coded</Text>
                      <Text style={styles.desktopEmptyText}>
                        Every subject currently has a code assigned.
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.desktopListBlock}>
                      {subjectsMissingCode.map((subject, index) => (
                        <Pressable
                          key={subject.id}
                          onPress={() => openEditModal(subject)}
                          style={[
                            styles.desktopListRow,
                            index < subjectsMissingCode.length - 1
                              ? styles.desktopListDivider
                              : null,
                          ]}>
                          <View style={styles.desktopRowIndex}>
                            <Text style={styles.desktopRowIndexText}>
                              {subject.name.slice(0, 1).toUpperCase()}
                            </Text>
                          </View>

                          <View style={styles.desktopRowCopy}>
                            <Text style={styles.desktopRowTitle} numberOfLines={1}>
                              {subject.name}
                            </Text>
                            <Text style={styles.desktopRowMeta} numberOfLines={1}>
                              Missing code
                            </Text>
                          </View>

                          <View style={styles.desktopRowRight}>
                            <View style={[styles.desktopRowTag, { backgroundColor: '#EEF4EF' }]}>
                              <Text style={[styles.desktopRowTagText, { color: '#5D7261' }]}>
                                Open
                              </Text>
                            </View>
                            <Text style={styles.desktopRowCount}>Add code</Text>
                          </View>
                        </Pressable>
                      ))}
                    </View>
                  )}
                </View>
              </View>
            </View>
          </ScrollView>
        </View>
        {desktopModalSheet}
      </>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea}>
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: Math.max(insets.bottom, 20) + 88 },
          ]}
          showsVerticalScrollIndicator={false}>
          <View style={styles.heroShell}>
            <View style={styles.heroPanel}>
              <View style={styles.heroHeaderRow}>
                <Text style={styles.heroKicker}>Admin / Subjects</Text>
                <Pressable
                  onPress={openCreateModal}
                  style={({ pressed }) => [styles.heroButton, pressed && styles.pressed]}>
                  <MaterialIcons name="add" size={18} color="#163828" />
                  <Text style={styles.heroButtonText}>Add subject</Text>
                </Pressable>
              </View>

              <View style={styles.heroBody}>
                <Text style={styles.heroTitle}>Manage your subject catalog from one screen.</Text>
                <Text style={styles.heroSubtitle}>
                  Keep names and codes clean so assignments and papers stay easy to scan.
                </Text>
              </View>

              <View style={styles.heroStatsRow}>
                {subjectStats.map((item, index) => (
                  <View
                    key={item.label}
                    style={[styles.heroStat, index > 0 ? styles.heroStatDivider : null]}>
                    <Text style={styles.heroStatValue}>{item.value}</Text>
                    <Text style={styles.heroStatLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.surface}>
            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>All Subjects</Text>
                <Pressable onPress={openCreateModal}>
                  <Text style={styles.linkText}>Add subject</Text>
                </Pressable>
              </View>

              {loading ? (
                <View style={styles.loadingWrap}>
                  <ActivityIndicator color="#163828" />
                </View>
              ) : subjects.length === 0 ? (
                <View style={styles.emptyState}>
                  <View style={styles.emptyIcon}>
                    <MaterialIcons name="library-books" size={28} color="#163828" />
                  </View>
                  <Text style={styles.emptyTitle}>No subjects found</Text>
                  <Text style={styles.emptyText}>
                    Create the first subject to start organizing the catalog.
                  </Text>
                  <Pressable onPress={openCreateModal} style={styles.emptyButton}>
                    <Text style={styles.emptyButtonText}>Create subject</Text>
                  </Pressable>
                </View>
              ) : (
                <View style={styles.listBlock}>
                  {subjects.map((subject, index) => (
                    <Pressable
                      key={subject.id}
                      onPress={() => openEditModal(subject)}
                      style={[
                        styles.listRow,
                        index < subjects.length - 1 ? styles.listDivider : null,
                      ]}>
                      <View style={styles.rowIndex}>
                        <Text style={styles.rowIndexText}>
                          {subject.name.slice(0, 1).toUpperCase()}
                        </Text>
                      </View>

                      <View style={styles.rowCopy}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {subject.name}
                        </Text>
                        <Text style={styles.rowMeta} numberOfLines={1}>
                          {subject.code?.trim() ? subject.code : 'No code'}
                        </Text>
                      </View>

                      <View style={styles.rowRight}>
                        <View style={styles.rowTag}>
                          <Text style={styles.rowTagText}>
                            {subject.code?.trim() ? 'Coded' : 'Open'}
                          </Text>
                        </View>
                        <Text style={styles.rowCount}>Tap to edit</Text>
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>

      <Modal animationType="slide" transparent visible={modalVisible} onRequestClose={closeModal}>
        <View style={styles.modalBackdrop}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.modalKeyboard}>
            <View style={styles.modalCard}>
              <ScrollView
                contentContainerStyle={styles.modalScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}>
                <View style={styles.modalHandle} />
                <Text style={styles.modalTitle}>{modalTitle}</Text>
                <Text style={styles.modalSubtitle}>
                  Add a short name and optional code for easier scanning.
                </Text>

                <InputField
                  autoCapitalize="words"
                  label="SUBJECT NAME"
                  onChangeText={setName}
                  placeholder="e.g. Mathematics"
                  value={name}
                />
                <InputField
                  autoCapitalize="characters"
                  label="SUBJECT CODE"
                  onChangeText={setCode}
                  placeholder="e.g. MATH-101"
                  value={code}
                />

                <View style={styles.modalActions}>
                  <Pressable onPress={closeModal} style={styles.cancelBtn}>
                    <Text style={styles.cancelBtnText}>Cancel</Text>
                  </Pressable>
                  <Pressable onPress={handleSubmit} style={styles.saveBtn}>
                    {submitting ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.saveBtnText}>
                        {modalMode === 'create' ? 'Save subject' : 'Update subject'}
                      </Text>
                    )}
                  </Pressable>
                </View>
              </ScrollView>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    </View>
  );
}

function InputField(props: React.ComponentProps<typeof TextInput> & { label: string }) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{props.label}</Text>
      <TextInput placeholderTextColor="#A8B9AA" style={styles.input} {...props} />
    </View>
  );
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

const styles = StyleSheet.create({
  desktopPage: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  desktopScrollView: {
    flex: 1,
  },
  desktopScroll: {
    padding: 28,
    gap: 24,
  },
  desktopHero: {
    gap: 18,
  },
  desktopHeroTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
  },
  desktopHeroCopy: {
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  desktopEyebrow: {
    color: '#6F7D71',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '800',
  },
  desktopTitle: {
    color: '#163828',
    fontSize: 34,
    lineHeight: 40,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopSubtitle: {
    color: '#6F7D71',
    fontSize: 14,
    lineHeight: 20,
  },
  desktopHeaderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#163828',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 12,
    minWidth: 150,
  },
  desktopHeaderBtnText: {
    color: '#F7F4EE',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopHeroPanel: {
    backgroundColor: '#103427',
    borderRadius: 28,
    padding: 22,
    gap: 18,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.09)',
  },
  desktopHeroPanelMain: {
    gap: 8,
    maxWidth: 560,
  },
  desktopHeroDate: {
    color: '#BBD0C2',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  desktopHeroHeadline: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopHeroBody: {
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 21,
    maxWidth: 620,
  },
  desktopStatsGrid: {
    flexDirection: 'row',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(244, 239, 230, 0.12)',
  },
  desktopStatCard: {
    flex: 1,
    paddingTop: 4,
    paddingBottom: 14,
  },
  desktopStatDivider: {
    paddingLeft: 14,
    marginLeft: 14,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(244, 239, 230, 0.12)',
  },
  desktopStatValue: {
    color: '#F7F4EE',
    fontSize: 24,
    lineHeight: 28,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  desktopStatLabel: {
    color: '#9EB5A7',
    fontSize: 12,
    marginTop: 4,
  },
  desktopGrid: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },
  desktopColumnLarge: {
    flex: 1.4,
    gap: 20,
    minWidth: 0,
  },
  desktopColumnSide: {
    flex: 0.9,
    gap: 20,
    minWidth: 0,
  },
  desktopSection: {
    gap: 14,
  },
  sectionHeadDesktop: {
    gap: 4,
  },
  sectionTitleDesktop: {
    color: '#163828',
    fontSize: 20,
    lineHeight: 24,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  sectionCaptionDesktop: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  linkTextDesktop: {
    color: '#47624F',
    fontSize: 13,
    fontWeight: '700',
  },
  desktopListBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  desktopListRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  desktopListDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  desktopRowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#F0F5F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopRowIndexText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  desktopRowCopy: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  desktopRowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopRowMeta: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  desktopRowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  desktopRowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  desktopRowTagText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopRowCount: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '700',
  },
  desktopLoadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopEmptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  desktopEmptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopEmptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopEmptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  desktopEmptyButton: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  desktopEmptyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(13, 22, 16, 0.62)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  desktopModalKeyboard: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopModalShell: {
    width: '100%',
    maxWidth: 760,
    borderRadius: 30,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    shadowColor: '#0E2817',
    shadowOpacity: 0.22,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 18 },
    elevation: 24,
  },
  desktopModalCard: {
    backgroundColor: '#FDFBF6',
    paddingHorizontal: 28,
    paddingVertical: 24,
  },
  desktopModalScroll: {
    gap: 18,
    paddingBottom: 4,
  },
  desktopModalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  desktopModalHeaderLabel: {
    color: '#6F7D71',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopModalHeaderValue: {
    color: '#163828',
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
    marginTop: 2,
  },
  desktopModalHeaderSubtext: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
    maxWidth: 520,
  },
  desktopModalClose: {
    width: 40,
    height: 40,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E7EEE7',
  },
  desktopModalForm: {
    gap: 16,
  },
  desktopModalActions: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 8,
  },
  desktopModalCancel: {
    flex: 1,
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
    borderWidth: 1,
    borderColor: '#E6ECE6',
  },
  desktopModalCancelText: {
    color: '#3C4A3E',
    fontSize: 13,
    fontWeight: '800',
  },
  desktopModalSave: {
    flex: 1.4,
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#163828',
  },
  desktopModalSaveText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  container: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  safeArea: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingTop: 10,
    gap: 18,
  },
  heroShell: {
    gap: 18,
  },
  heroPanel: {
    backgroundColor: '#163828',
    borderRadius: 28,
    padding: 18,
    gap: 18,
  },
  heroHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  heroBody: {
    gap: 8,
  },
  heroKicker: {
    color: '#B9CFBE',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 27,
    lineHeight: 33,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroSubtitle: {
    color: '#D4E4D7',
    fontSize: 14,
    lineHeight: 20,
  },
  heroButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#F4EFE6',
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  heroButtonText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  heroStatsRow: {
    flexDirection: 'row',
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  heroStat: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 10,
    gap: 3,
  },
  heroStatDivider: {
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255, 255, 255, 0.12)',
  },
  heroStatValue: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroStatLabel: {
    color: '#B9CFBE',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  surface: {
    gap: 18,
  },
  sectionBlock: {
    gap: 12,
  },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sectionTitle: {
    color: '#163828',
    fontSize: 18,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  sectionCaption: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
    flexShrink: 1,
    textAlign: 'right',
  },
  linkText: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  snapshotPanel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 16,
    gap: 14,
  },
  snapshotMain: {
    gap: 6,
  },
  snapshotLabel: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  snapshotValue: {
    color: '#163828',
    fontSize: 20,
    lineHeight: 24,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  snapshotMeta: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
  },
  snapshotButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#163828',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 14,
  },
  snapshotButtonText: {
    color: '#F4EFE6',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  listBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  listDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  rowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#F0F5F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowIndexText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  rowCopy: {
    flex: 1,
    gap: 2,
  },
  rowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  rowMeta: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  rowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  rowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#EAF3FF',
  },
  rowTagText: {
    color: '#0F4C81',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  rowCount: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '700',
  },
  loadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  emptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  emptyButton: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  modalKeyboard: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalCard: {
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    backgroundColor: '#FFFFFF',
    padding: 22,
    paddingBottom: 40,
    gap: 16,
    maxHeight: '88%',
  },
  modalScrollContent: {
    gap: 16,
    paddingBottom: 4,
  },
  modalHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#EDF2EE',
    alignSelf: 'center',
  },
  modalTitle: {
    color: '#163828',
    fontSize: 20,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  modalSubtitle: {
    color: '#6A8570',
    fontSize: 13,
    lineHeight: 19,
    marginTop: -8,
  },
  field: {
    gap: 8,
  },
  fieldLabel: {
    color: '#163828',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    fontFamily: Fonts.sans,
  },
  input: {
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#E4E8E4',
    backgroundColor: '#FAFBFA',
    paddingHorizontal: 14,
    color: '#1C1C1E',
    fontSize: 15,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  cancelBtn: {
    flex: 1,
    minHeight: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
    borderWidth: 1,
    borderColor: '#E6ECE6',
  },
  cancelBtnText: {
    color: '#3C4A3E',
    fontSize: 12,
    fontWeight: '800',
  },
  saveBtn: {
    flex: 2,
    minHeight: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#163828',
  },
  saveBtnText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
  },
  pressed: {
    opacity: 0.82,
    transform: [{ scale: 0.99 }],
  },
});
